import React, { ReactNode } from 'react';
import styled from '@emotion/styled';
import classNames from 'classnames';

import { ColorWrapChangeHandler } from 'react-color/lib/components/common/ColorWrap';
import { MIXIN_SCREEN_READER_TEXT } from '../Style/mixins';
import { INPUT_DESCRIPTION } from '../Style/input';
import { CustomColourPicker } from './CustomColourPicker';
import { OL_COLOUR_BLACK_10, OL_COLOUR_BLACK_55 } from '../Style/colours';
import { convertColorToString } from './helpers';
import { InputLabel } from '../InputLabel/component';
import { AutoSaveState } from '../AutoSaveIndicator/component';

declare interface ColourPickerProps {
  label: string | ReactNode;
  description?: ReactNode;
  className?: string;
  disabled?: boolean;
  value?: string;
  error?: string | null;
  required?: boolean;
  onChange: (value: string) => void;
  onDrag?: (value: string) => void;
  onClose?: (value: string | null) => void;
  presetColors?: string[] | null;
  autoSave?: boolean;
  autoSaveState?: AutoSaveState;
}

interface ColourPickerState {
  displayColourPicker: boolean;
}

interface ColorButtonExtraProps {
  disabled: boolean;
  pressed: boolean;
}

interface ColorButtonLabelExtraProps {
  value: string;
}

const Description = styled.span(INPUT_DESCRIPTION);

const ColorButton = styled.button<ColorButtonExtraProps>(
  ({ disabled, pressed }) => ({
    padding: 5,
    background: '#fff',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: disabled || pressed ? OL_COLOUR_BLACK_10 : OL_COLOUR_BLACK_55,
    borderRadius: 1,
    display: 'inline-block',
    cursor: disabled ? 'default' : 'pointer',
  })
);

const ColorButtonLabel = styled.div<ColorButtonLabelExtraProps>(
  ({ value }) => ({
    background: value,
    boxShadow: `0 0 0 1px inset ${OL_COLOUR_BLACK_10}`,
    width: 36,
    height: 14,
    borderRadius: 2,
  })
);

const ColorButtonLabelText = styled.span(MIXIN_SCREEN_READER_TEXT);

const ColorPickerBackDrop = styled.div({
  position: 'fixed',
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 2,
});

const ColorPickerBackDropText = styled.span(MIXIN_SCREEN_READER_TEXT);

export class ColourPicker extends React.PureComponent<ColourPickerProps> {
  public state: ColourPickerState = {
    displayColourPicker: false,
  };

  private handleButtonClick = (): void => {
    this.setState((state: ColourPickerState) => ({
      displayColourPicker: !state.displayColourPicker,
    }));
  };

  private handleCloseClick = (): void => {
    const { onClose, value } = this.props;

    this.setState(() => ({
      displayColourPicker: false,
    }));

    if (onClose) {
      onClose(value || null);
    }
  };

  private handleChange: ColorWrapChangeHandler = (color): void => {
    const { onChange } = this.props;
    onChange(convertColorToString(color));
  };

  private handleDrag: ColorWrapChangeHandler = (color): void => {
    const { onDrag } = this.props;
    if (onDrag) {
      onDrag(convertColorToString(color));
    }
  };

  private handleBackDropKeyPress = (
    event: React.KeyboardEvent<HTMLInputElement>
  ): void => {
    // when 'esc' key pressed
    if (event.keyCode === 27) {
      event.preventDefault();
      this.handleCloseClick();
    }
  };

  public render(): ReactNode {
    const {
      label,
      description,
      error,
      value,
      required = false,
      disabled: buttonDisabled = false,
      className,
      presetColors,
      autoSave,
      autoSaveState,
    } = this.props;
    const { displayColourPicker } = this.state;

    return (
      <div className={classNames(className)}>
        {label && (
          <InputLabel
            required={required}
            error={error}
            autoSave={autoSave}
            autoSaveState={autoSaveState}
          >
            {label}
          </InputLabel>
        )}

        {description && <Description>{description}</Description>}
        <ColorButton
          disabled={buttonDisabled}
          pressed={displayColourPicker}
          type="button"
          onClick={this.handleButtonClick}
        >
          <ColorButtonLabel value={value || ''}>
            <ColorButtonLabelText>{value}</ColorButtonLabelText>
          </ColorButtonLabel>
        </ColorButton>

        {displayColourPicker ? (
          <>
            <ColorPickerBackDrop
              role="button"
              tabIndex={-1}
              onClick={this.handleCloseClick}
              onKeyPress={this.handleBackDropKeyPress}
            >
              <ColorPickerBackDropText>
                Close colour picker
              </ColorPickerBackDropText>
            </ColorPickerBackDrop>
            <CustomColourPicker
              color={value || ''}
              onChangeComplete={this.handleChange}
              onChange={this.handleDrag}
              onClose={this.handleCloseClick}
              presetColors={presetColors}
            />
          </>
        ) : null}
      </div>
    );
  }
}
